import React, { useMemo, useEffect, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import useFetchAction from '../../../hooks/useFetchAction';
import {
  getForm as getFormAction,
  addField as addFieldAction,
  updateField as updateFieldAction,
  deleteField as deleteFieldAction,
  addBlock as addBlockAction,
  moveBlock as moveBlockAction,
  updateBlock as updateBlockAction,
  deleteBlock as deleteBlockAction,
} from '../../../store/actions/form';
import Spinner from '../../atoms/Spinner/Spinner';
import styles from './WYSIWYGGridPage.css';
import DraggableGrid from '../../organisms/Grid/DraggableGrid';
import { useActions } from '../../../hooks/useActions';
import {
  currentFormSelector,
  fieldBoxesSelector,
  focusedFieldSelector,
  blocksBoxesSelector,
  allBoxesSelector,
  focusedBlockIdSelector,
  lowerBoxYSelector,
  selectModeSelector,
  fieldHasNoCompareRuleSelector,
  fieldHasCompareRuleSelector,
  fieldHasSortingRuleSelector,
  fieldHasHideIfRuleSelector,
  focusedBlockSelector,
} from '../../../store/selectors/editForm';
import {
  setFocusedField as setFocusedFieldAction,
  setFocusedBlock as setFocusedBlockAction,
  addSelectFields as addSelectFieldsAction,
} from '../../../store/actions/editForm';
import { getTypologyInitialValues } from '../../../helpers/typology';
import BlockBox from '../../organisms/WirkGrid/BlockBox/BlockBox';
import FieldBox from '../../organisms/WirkGrid/FieldBox/FieldBox';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWHEIGHT,
  GRID_GUTTER,
  GRID_MIN_ROWS,
} from '../../../constants/grid';
import { PATH_BLOCKS_FORM, PATH_DATA_FORM, PATH_EDITFIELDS_FORM } from '../../../constants/paths';
import BoxPreview from '../../molecules/BoxPreview/BoxPreview';
import { useNavigate, useParams } from 'react-router';
import { populatePath } from '../../../helpers/path';
import { FIELD, FIELD_STUB, BLOCK_STUB, BLOCK } from '../../../constants/draggableType';
import { COLOR_LIGHTYELLOW } from '../../../constants/colors';
import { getTypologyKind } from '../../../helpers/typology';
import { DATA } from '../../../constants/typology/data';
import SVG from '../../atoms/SVG/SVG';
import { bem } from '../../../helpers/styles';
import { selectFieldsSelector } from '../../../store/selectors/editForm';
import { fieldHasRuleSelector } from '../../../store/selectors/editForm';
import { createToast } from '../../../store/actions/toast';
import { ERROR } from '../../../constants/toastType';
import useTranslate from '../../../hooks/useTranslate';
import { isInside } from '../../../helpers/formTree';
import useQuery from '../../../hooks/useQuery';
import FormStyle from '../../atoms/FormStyle';
import CopyPasteGridItem from '../../organisms/Grid/CopyPasteGridItem';
import DuplicateBlockControls from '../../organisms/DuplicateBlockControls/DuplicateBlockControls';
import { getBlockDarkerColor } from '../../../helpers/color';

const WYSIWYGGridPage = ({ path }) => {
  const i18n = useTranslate();
  const navigate = useNavigate();
  const { formId } = useParams();
  const form = useSelector(currentFormSelector);
  const focusedField = useSelector(focusedFieldSelector);
  const focusedBlockId = useSelector(focusedBlockIdSelector);
  const fieldsBoxes = useSelector(fieldBoxesSelector);
  const blocksBoxes = useSelector(blocksBoxesSelector);
  const allItems = useSelector(allBoxesSelector);
  const lowerY = useSelector(lowerBoxYSelector);
  const selectMode = useSelector(selectModeSelector);
  const selectFields = useSelector(selectFieldsSelector);
  const fieldHasRule = useSelector(fieldHasRuleSelector);
  const fieldHasNoCompareRule = useSelector(fieldHasNoCompareRuleSelector);
  const fieldHasCompareRule = useSelector(fieldHasCompareRuleSelector);
  const fieldHasSortingRule = useSelector(fieldHasSortingRuleSelector);
  const fieldHasHideIfRule = useSelector(fieldHasHideIfRuleSelector);
  const query = useQuery();
  const [hiddenBoxes, setHiddenBoxes] = useState([]);
  const [draggingBlockId, setDraggingBlockId] = useState(null);

  const scrollOffsetRef = useRef({ x: 0, y: 0 });
  const getScrollOffset = useCallback(() => scrollOffsetRef.current, []);

  useEffect(() => {
    const mainElement = document.getElementById('app-main');
    if (!mainElement) return;

    const handleScroll = (event) => {
      scrollOffsetRef.current = {
        y: event?.target?.scrollTop || 0,
        x: event?.target?.scrollLeft || 0,
      };
    };

    mainElement.addEventListener('scroll', handleScroll);
    return () => mainElement.removeEventListener('scroll', handleScroll);
  }, []);

  const [updateBlock] = useActions([updateBlockAction]);

  const [getForm, loading, error] = useFetchAction(getFormAction);
  const [
    addField,
    updateField,
    setFocusedField,
    setFocusedBlock,
    deleteField,
    addBlock,
    moveBlock,
    addSelectFields,
  ] = useActions([
    addFieldAction,
    updateFieldAction,
    setFocusedFieldAction,
    setFocusedBlockAction,
    deleteFieldAction,
    addBlockAction,
    moveBlockAction,
    addSelectFieldsAction,
  ]);
  const focusedBlock = useSelector(focusedBlockSelector);
  const [deleteBlock] = useActions([deleteBlockAction]);

  const gridColumns = form?.gridSize || DEFAULT_GRID_COLUMNS;
  const gridRowHeight = (DEFAULT_GRID_COLUMNS * DEFAULT_GRID_ROWHEIGHT) / form?.gridSize;

  // Compute what is draggable based on current path
  const [fieldDraggable, blockDraggable] = useMemo(() => {
    switch (path) {
      case PATH_DATA_FORM:
      case PATH_EDITFIELDS_FORM:
        return [true, false];
      case PATH_BLOCKS_FORM:
        return [false, true];
      default:
        return [false, false];
    }
  }, [path]);

  const dragRef = useRef();

  // Fetch form on page load
  useEffect(() => {
    if (!form) {
      getForm(formId);
    }
  }, []);

  // Check deletion constraints
  const handleDelete = (form, field) => {
    if (form) {
      if (fieldHasRule(field.id)) {
        createToast(ERROR, i18n('toast.rule', { fieldName: field.label }));
        return;
      }
      const sorting = fieldHasSortingRule(field.id);
      if (sorting) {
        createToast(ERROR, i18n('toast.sorting', { sortingName: sorting.name }));
        return;
      }
      if (fieldHasNoCompareRule(field.id) || fieldHasCompareRule(field.id)) {
        createToast(ERROR, i18n('toast.compare'));
        return;
      }
      const hideIf = fieldHasHideIfRule(field.id);
      if (hideIf) {
        createToast(ERROR, i18n('toast.hideIf', { fieldName: hideIf.label || field.id }));
        return;
      }
      deleteField(form.id, field.id);
    }
  };
  // When a block is clicked, we redirect to appropriate page
  const handleBlockClick = useCallback(
    (blockId) => {
      setFocusedBlock(blockId);
      setFocusedField(null);
      // Redirect to blocks page if needed
      if (path !== PATH_BLOCKS_FORM) {
        navigate(populatePath(PATH_BLOCKS_FORM, { formId: form.id }, query));
      }
    },
    [navigate, form, path],
  );

  // When a field is clicked, we redirect to appropriate page
  const handleFieldClick = useCallback(
    (field) => {
      setFocusedField(field.id);
      setFocusedBlock(null);
      const neededPath =
        getTypologyKind(field.type) === DATA ? PATH_DATA_FORM : PATH_EDITFIELDS_FORM;
      // Redirect to fields or data page if needed
      if (neededPath !== path) {
        navigate(populatePath(neededPath, { formId: form.id }, query));
      }
    },
    [navigate, form, path],
  );

  const handleFieldClickSelectMode = useCallback(
    (field) => {
      if (selectMode) {
        const duplicate = selectFields ? selectFields?.find((el) => el.id === field.id) : null;
        if (!duplicate) {
          addSelectFields({
            label: field.type,
            type: field.type,
            id: field.id,
            box: field.box,
            multi: field.multi,
          });
        }
      }
    },
    [selectFields, selectMode],
  );

  // Add / update fields in state when they are droped
  const handleDropItem = useCallback(
    (item, position) => {
      const box = {
        x: typeof position.x !== 'undefined' ? position.x : item.x,
        y: typeof position.y !== 'undefined' ? position.y : item.y,
        width: item.width || 1,
        height: item.height || 1,
        ...position,
      };

      switch (item.type) {
        case FIELD_STUB:
          addField(formId, {
            type: item.fieldType,
            box,
            data: getTypologyInitialValues(item.fieldType),
          });
          setFocusedBlock(null);
          break;
        case FIELD:
          updateField(formId, item.id, {
            box,
          });
          setFocusedBlock(null);
          break;
        case BLOCK_STUB:
          addBlock(form.id, {
            color: COLOR_LIGHTYELLOW,
            name: `${i18n('BlockForm.block')} ${blocksBoxes.length + 1}`,
            box,
          });
          setFocusedField(null);
          break;
        case BLOCK:
          moveBlock(form.id, item.id, {
            box,
          });
          setFocusedField(null);
          break;
        default:
          break;
      }
    },
    [form],
  );

  const handleBlockMoveStart = useCallback(
    (item) => {
      const draggedBlock = blocksBoxes.find((block) => block.id === item.id);

      const childFieldsIds = fieldsBoxes
        .filter((field) => isInside(draggedBlock.box, field.box))
        .map((field) => field.id);

      const childBlocksIds = blocksBoxes
        .filter((block) => block.id !== draggedBlock.id && isInside(draggedBlock.box, block.box))
        .map((block) => block.id);

      setHiddenBoxes([...childFieldsIds, ...childBlocksIds]);
      setDraggingBlockId(item.id);
    },
    [blocksBoxes, fieldsBoxes],
  );

  const handleBlockMoveEnd = () => {
    setHiddenBoxes([]);
    setDraggingBlockId(null);
  };

  const handleBlockClickWithId = useCallback(
    (blockId) => () => handleBlockClick(blockId),
    [handleBlockClick],
  );

  const handleDeleteWithFieldBox = useCallback(
    (fieldBox) => () => handleDelete(form, fieldBox),
    [form],
  );

  const handleFieldBoxClick = useCallback(
    (fieldBox) => () => {
      return selectMode ? handleFieldClickSelectMode(fieldBox) : handleFieldClick(fieldBox);
    },
    [selectMode, handleFieldClickSelectMode, handleFieldClick],
  );

  if (error) {
    return <p>Error</p>;
  }

  if (!form || loading) {
    return <Spinner />;
  }

  return (
    <FormStyle form={form}>
      {form.showFormTitle === true ? (
        <div className={styles.WYSIWYGGridPage__title}>{form.name}</div>
      ) : null}

      <DraggableGrid
        getScrollOffset={getScrollOffset}
        columns={gridColumns}
        rowHeight={gridRowHeight}
        gutter={GRID_GUTTER}
        accept={[FIELD, FIELD_STUB, BLOCK, BLOCK_STUB]}
        fixedRows={lowerY + GRID_MIN_ROWS}
        items={allItems}
        onDropItem={handleDropItem}
        renderPreview={BoxPreview}
        className={styles.WYSIWYGGridPage}
        dotBackground
        ref={dragRef}
      >
        <div
          className={bem(styles, 'WYSIWYGGridPage__emptyState', { visible: allItems.length === 0 })}
        >
          <SVG glyph="home_empty" className={styles.WYSIWYGGridPage__emptySVG} />
        </div>
        <CopyPasteGridItem
          formId={formId}
          focusedBlock={focusedBlock}
          focusedField={focusedField}
          blocksBoxes={blocksBoxes}
          fieldsBoxes={fieldsBoxes}
          allItems={allItems}
          gridColumns={gridColumns}
          dragRef={dragRef}
          getScrollOffset={getScrollOffset}
          addField={addField}
          addBlock={addBlock}
          updateBlock={updateBlock}
          updateField={updateField}
          setFocusedBlock={setFocusedBlock}
          setFocusedField={setFocusedField}
          deleteField={deleteField}
          deleteBlock={deleteBlock}
        />
        {blocksBoxes.map((blockBox, i) =>
          !hiddenBoxes.includes(blockBox.id) ? (
            <BlockBox
              blockBox={blockBox}
              rowHeight={gridRowHeight}
              key={i}
              draggable={blockDraggable}
              focused={focusedBlockId === blockBox.id}
              onClick={handleBlockClickWithId(blockBox.id)}
              onMoveStart={handleBlockMoveStart}
              onMoveEnd={handleBlockMoveEnd}
            />
          ) : null,
        )}
        {fieldsBoxes.map((fieldBox, i) =>
          !hiddenBoxes.includes(fieldBox.id) ? (
            <FieldBox
              fieldBox={fieldBox}
              key={i}
              draggable={fieldDraggable}
              focused={focusedField && focusedField.id === fieldBox.id}
              onDelete={handleDeleteWithFieldBox(fieldBox)}
              onClick={handleFieldBoxClick(fieldBox)}
            />
          ) : null,
        )}
        {/* "Fake" duplicate wrappers */}
        {blocksBoxes
          .filter(
            (blockBox) => blockBox.duplicable
            && !hiddenBoxes.includes(blockBox.id)
            && blockBox.id !== draggingBlockId,
          )
          .map((blockBox) => {
            const borderColor = getBlockDarkerColor(blockBox.color, 0.5);
            return (
              <div
                key={`wrapper-${blockBox.id}`}
                className={styles.WYSIWYGGridPage__fakeWrapper}
                style={{
                  top: blockBox.item.y * gridRowHeight + (gridRowHeight + 2),
                  left: `calc(${(blockBox.item.x / gridColumns) * 100}% + ${GRID_GUTTER}px)`,
                  width: `calc(${(blockBox.item.width / gridColumns) * 100}% - ${GRID_GUTTER*2}px)`,
                  borderTop: `1px solid ${borderColor}`,
                }}
              >
                <DuplicateBlockControls
                  color={blockBox.color}
                  showIncrement={blockBox.showIncrement}
                />
              </div>
            );
          })}
      </DraggableGrid>
    </FormStyle>
  );
};

WYSIWYGGridPage.propTypes = {
  path: PropTypes.string,
};

export default WYSIWYGGridPage;
